import { useEffect, useRef, useState } from 'react';

import classnames from 'classnames';
import { useAtom } from 'jotai';

import { DealerSelectButton, useSelectedDealerId } from '@lotlinx-aws/dealer-selector';
import ControlPanel from 'pages/MarketMaps/ControlPanel';
import HexMarketMap from 'pages/MarketMaps/HexMarketMap';
import InStoreShoppersMap from 'pages/MarketMaps/InStoreShoppersMap';
import Chip from '@lotlinx-aws/sparky-component-library/Sparky/Chip';
import IconButton from '@lotlinx-aws/sparky-component-library/Sparky/IconButton';
import { CloseIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/CloseIcon';
import { LotlinxXIcon } from '@lotlinx-aws/sparky-component-library/Sparky/SVGs/LotlinxXIcon';
import Text from '@lotlinx-aws/sparky-component-library/Sparky/Text';

import styles from './MarketMaps.module.scss';
import useAccess from 'hooks/useAccess';
import { AccountType } from 'types/User';
import { userAtom } from 'store';

import { MARKET_MAPS_DEFS, type MarketMapsKeys } from './MARKET_MAPS_DEFS';
import { showMarketMapsAtom } from './showMarketMapsAtom';

export type { MarketMapsKeys } from './MARKET_MAPS_DEFS';

/**
 * MarketMaps
 */
export default function MarketMaps() {
  const [showMarketMaps, setShowMarketMaps] = useAtom(showMarketMapsAtom);
  const [activeMetric, setActiveMetric] = useState<MarketMapsKeys>('inStoreTraffic');
  const [dealerId] = useSelectedDealerId();
  const formRef = useRef<HTMLInputElement>(null);
  const [user] = useAtom(userAtom);
  const { isVmbiSubscribedOnly } = useAccess();

  useEffect(() => {
    formRef?.current?.click();
  }, [formRef, dealerId]);

  useEffect(() => {
    if (isVmbiSubscribedOnly) {
      setActiveMetric('inMarketShoppers');
    }
  }, [isVmbiSubscribedOnly]);

  const MetricSelectors = () => {
    return (
      <div className={styles.metricSelectors}>
        {Object.keys(MARKET_MAPS_DEFS).map((key) => {
          if (key === 'inStoreTraffic' && isVmbiSubscribedOnly) {
            return <></>;
          }
          return (
            <button
              key={key}
              onClick={() => {
                setActiveMetric(key as MarketMapsKeys);
              }}
            >
              <Chip
                className={classnames([
                  styles.metricSelectorsButton,
                  key === activeMetric ? styles.active : styles.inactive,
                ])}
                variant='filled'
              >
                {MARKET_MAPS_DEFS[key as MarketMapsKeys].title}
              </Chip>
            </button>
          );
        })}
      </div>
    );
  };

  const CloseButton = () => {
    return (
      <IconButton
        aria-label='close market maps modal button'
        onClick={() => setShowMarketMaps(false)}
        className={styles.closeButton}
        size='md'
      >
        <CloseIcon />
      </IconButton>
    );
  };

  if (!showMarketMaps) return null;

  return (
    <div
      className={styles.overlay}
      onClick={() => setShowMarketMaps(false)}
      aria-label='Lexica Market Maps'
    >
      <div className={styles.marketmaps} onClick={(e) => e.stopPropagation()}>
        <div className={styles.titleContainer}>
          <LotlinxXIcon />
          <Text size='20' fontWeight={7} color='navy'>
            Market Maps
          </Text>
        </div>
        <MetricSelectors />
        <ControlPanel activeMetric={activeMetric} />
        <DealerSelectButton
          isMarketMap
          align='end'
          className={styles.dealerSelectContainer}
          appId='Turn4'
          isInternalUser={user?.accountType === AccountType.OWNER}
        />

        <CloseButton />
        {activeMetric === 'inStoreTraffic' ? (
          <InStoreShoppersMap />
        ) : (
          <HexMarketMap activeMetric={activeMetric} />
        )}
      </div>
    </div>
  );
}
